import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Loader2, Send, Navigation } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { Reveal, ChapterTag } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const mapQuery = lang === "ar" ? "شركة برق سيف لحلول الأعمال، القصر مول، السويدي، الرياض" : "Barq Safe Business Solutions, Al Qasr Mall, As Suwaidi, Riyadh";
  const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&hl=${lang}&z=17&output=embed`;
  const directionsUrl = "https://maps.app.goo.gl/eHUD82CKFF6oPs5L8";

  const services = [...t("services").map((s) => s.name), t("service_investor"), t("service_other")];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, email: form.email || null });
      toast.success(t("form_success"));
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t("form_error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-navy-900/70 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold/40 transition-[border-color,box-shadow] duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
      <Reveal>
        <ChapterTag num="08" label={t("ch_contact")} />
        <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight">{t("contact_title")}</h2>
        <p className="mt-4 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">{t("contact_sub")}</p>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-2 space-y-5">
          {[
            { icon: Phone, label: t("contact_call"), value: "+966 57 946 2600", href: "tel:+966579462600", testid: "contact-phone-link", dirLtr: true },
            { icon: WhatsAppIcon, label: t("contact_whatsapp"), value: "+966 57 946 2600", href: "https://wa.me/966579462600", testid: "contact-whatsapp-link", dirLtr: true },
            { icon: Mail, label: t("contact_email"), value: "barq.safe.sa@gmail.com", href: "mailto:barq.safe.sa@gmail.com", testid: "contact-email-link", dirLtr: true },
            { icon: MapPin, label: t("contact_location"), value: t("contact_location_value"), href: null, testid: "contact-location", dirLtr: false },
          ].map((c) => {
            const Icon = c.icon;
            const inner = (
              <div className="flex items-center gap-4 glass rounded-2xl p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40" data-testid={c.testid}>
                <div className="w-11 h-11 rounded-xl bg-gold/10 border border-gold/25 flex items-center justify-center text-gold shrink-0">
                  <Icon size={20} />
                </div>
                <div>
                  <div className="text-xs text-white/50">{c.label}</div>
                  <div className="font-semibold text-sm md:text-base mt-0.5" dir={c.dirLtr ? "ltr" : undefined}>{c.value}</div>
                </div>
              </div>
            );
            return c.href ? <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{inner}</a> : <div key={c.label}>{inner}</div>;
          })}
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form onSubmit={submit} className="glass rounded-3xl p-8 md:p-10 space-y-5" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-5">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("form_name")} className={inputCls} data-testid="contact-name-input" />
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder={t("form_phone")} dir="ltr" className={`${inputCls} text-left rtl:text-right`} data-testid="contact-phone-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("form_email")} dir="ltr" className={`${inputCls} text-left rtl:text-right`} data-testid="contact-email-input" />
              <select required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={`${inputCls} appearance-none`} data-testid="contact-service-select">
                <option value="" disabled>{t("form_service")}</option>
                {services.map((s) => <option key={s} value={s} className="bg-navy-800">{s}</option>)}
              </select>
            </div>
            <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t("form_message")} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            <button type="submit" disabled={sending} data-testid="contact-submit-button"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-gold text-navy-900 font-bold hover:bg-gold-light disabled:opacity-60 transition-colors duration-300 shadow-xl shadow-gold/20">
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? t("form_sending") : t("form_submit")}
            </button>
          </form>
        </Reveal>
      </div>

      <Reveal delay={0.2}>
        <div className="mt-10 relative rounded-3xl overflow-hidden border border-gold/20 shadow-2xl shadow-navy-950" data-testid="map-card">
          <iframe
            key={lang}
            src={mapUrl}
            title={t("map_title")}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-[340px] md:h-[420px] border-0 grayscale-[30%] contrast-[1.05]"
            data-testid="map-iframe"
          />
          <div className="absolute top-5 start-5 glass rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center text-gold shrink-0">
                <MapPin size={18} />
              </div>
              <div>
                <div className="text-xs text-white/50">{t("map_title")}</div>
                <div className="text-sm font-semibold mt-0.5">{t("contact_location_value")}</div>
              </div>
            </div>
            <a href={directionsUrl} target="_blank" rel="noreferrer" data-testid="map-directions-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gold text-navy-900 text-xs font-bold hover:bg-gold-light transition-colors">
              <Navigation size={13} />
              {t("map_directions")}
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
